import React, { useEffect, useState } from "react";
import API_BASE from "../config/api";
import { adminFetch } from "../utils/adminAuth";

/*
  CR U1 — "Admin page management: manage pages the same way as the
  Business Website's 'manage website' section — same layout and tab
  structure." This mirrors that pattern at a smaller scale: one tab per
  static site page (About Us, Contact Us today — more can be added the
  same way later), each editing the actual prose/contact-info content
  rather than the whole hand-built page layout.

  Scope note: About Us / Contact Us are richly laid out pages (hero,
  stat cards, mission/vision, feature grid, map). Rather than build a
  full page-builder, this makes the TEXT CONTENT of those pages
  admin-editable — same relationship BusinessWebsiteManager has to the
  business site template (the template is code, the content is data).
*/

const ABOUT_DEFAULT = {
    heroTitle: "About DialNepal",
    heroSubtitle: "Nepal's fastest-growing digital local search engine helping millions discover trusted businesses and services within seconds.",
    aboutText: "DialNepal is Nepal's most trusted local search and discovery platform, designed to help people find businesses, services, and professionals effortlessly.\n\nWe aim to empower local businesses by giving them a strong online presence and enabling them to connect with the right audience.",
    stat1Num: "50,000+", stat1Label: "Verified Listings",
    stat2Num: "120+", stat2Label: "Business Categories",
    stat3Num: "1M+", stat3Label: "Monthly Searches",
    stat4Num: "24/7", stat4Label: "Instant Access",
    missionText: "To build Nepal's largest and most reliable platform that helps people connect with businesses instantly with accuracy, trust, and transparency.",
    visionText: "To transform Nepal's local business ecosystem through digital innovation — empowering users with information and businesses with visibility.",
};

const CONTACT_DEFAULT = {
    heading: "Contact Us",
    subheading: "We're here to help! Reach out to us using the form below or via our contact details.",
    address: "Nepalgunj Banke, Bheri, Lumbini Province, Nepal",
    phone: "+977-9848427993",
    email: "support@dialnepal.com.np",
    hours: "Mon – Fri : 9:00 AM – 6:00 PM",
    mapEmbedUrl: "https://maps.google.com/maps?q=Kathmandu&t=&z=13&ie=UTF8&iwloc=&output=embed",
};

function PageForm({ pageKey, defaults, fields }) {
    const [form, setForm] = useState(defaults);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);

    useEffect(() => {
        adminFetch(`${API_BASE}/api/get_site_page.php?key=${pageKey}`)
            .then((r) => r.json())
            .then((res) => { if (res.success && res.content) setForm({ ...defaults, ...res.content }); })
            .finally(() => setLoading(false));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [pageKey]);

    const set = (k, v) => setForm((s) => ({ ...s, [k]: v }));

    const handleSave = async () => {
        setSaving(true);
        setMsg(null);
        try {
            const res = await adminFetch(`${API_BASE}/api/save_site_page.php`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ key: pageKey, content: form }),
            });
            const data = await res.json();
            setSaving(false);
            setMsg(data.success
                ? { type: "success", text: "Saved. The live page will reflect this immediately." }
                : { type: "danger", text: data.message || "Could not save." });
        } catch {
            setSaving(false);
            setMsg({ type: "danger", text: "Network error." });
        }
    };

    if (loading) return <div className="p-3">Loading...</div>;

    return (
        <div>
            {msg && <div className={`alert alert-${msg.type} py-2`}>{msg.text}</div>}
            <div className="row g-3">
                {fields.map((f) => (
                    <div className={f.wide ? "col-12" : "col-md-6"} key={f.key}>
                        <label className="form-label small">{f.label}</label>
                        {f.textarea ? (
                            <textarea className="form-control form-control-sm" rows={f.rows || 3} value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
                        ) : (
                            <input className="form-control form-control-sm" value={form[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} />
                        )}
                    </div>
                ))}
            </div>
            <div className="d-flex justify-content-end mt-3">
                <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? "Saving..." : "Save Page"}</button>
            </div>
        </div>
    );
}

const ABOUT_FIELDS = [
    { key: "heroTitle", label: "Hero Title" },
    { key: "heroSubtitle", label: "Hero Subtitle", textarea: true, rows: 2, wide: true },
    { key: "aboutText", label: "Who We Are (paragraphs, blank line = new paragraph)", textarea: true, rows: 6, wide: true },
    { key: "stat1Num", label: "Stat 1 Number" }, { key: "stat1Label", label: "Stat 1 Label" },
    { key: "stat2Num", label: "Stat 2 Number" }, { key: "stat2Label", label: "Stat 2 Label" },
    { key: "stat3Num", label: "Stat 3 Number" }, { key: "stat3Label", label: "Stat 3 Label" },
    { key: "stat4Num", label: "Stat 4 Number" }, { key: "stat4Label", label: "Stat 4 Label" },
    { key: "missionText", label: "Our Mission", textarea: true, rows: 3, wide: true },
    { key: "visionText", label: "Our Vision", textarea: true, rows: 3, wide: true },
];

const CONTACT_FIELDS = [
    { key: "heading", label: "Page Heading" },
    { key: "subheading", label: "Subheading", wide: true },
    { key: "address", label: "Address", wide: true },
    { key: "phone", label: "Phone" },
    { key: "email", label: "Email" },
    { key: "hours", label: "Working Hours" },
    { key: "mapEmbedUrl", label: "Google Maps Embed URL", wide: true },
];

export default function AdminPageManagement() {
    const [tab, setTab] = useState("about");

    return (
        <div>
            <h5 className="fw-bold mb-1">Page Management</h5>
            <p className="text-secondary small mb-3">Edit the text content shown on the public About Us and Contact Us pages.</p>

            <ul className="nav nav-tabs mb-3">
                <li className="nav-item"><button className={`nav-link ${tab === "about" ? "active" : ""}`} onClick={() => setTab("about")}>About Us</button></li>
                <li className="nav-item"><button className={`nav-link ${tab === "contact" ? "active" : ""}`} onClick={() => setTab("contact")}>Contact Us</button></li>
            </ul>

            {tab === "about" && <PageForm pageKey="about" defaults={ABOUT_DEFAULT} fields={ABOUT_FIELDS} />}
            {tab === "contact" && <PageForm pageKey="contact" defaults={CONTACT_DEFAULT} fields={CONTACT_FIELDS} />}
        </div>
    );
}
